import { QuoteVariableType } from '@sequencehq/quote-content'
import { match } from 'ts-pattern'
import { getVariableLabel } from '../../utils/quoteVariables'
import { blocknoteSchema } from '../blocknoteSchema'
import {
  InlineContent,
  isTableCell,
  Link,
  StyledText,
  StyleSchema
} from '@blocknote/core'

type BlocknoteInlineContent = InlineContent<
  (typeof blocknoteSchema)['inlineContentSchema'],
  (typeof blocknoteSchema)['styleSchema']
>

type BlocknoteBlock = (typeof blocknoteSchema.BlockNoteEditor)['document'][0]
export const getBlockUpdate = (
  block: BlocknoteBlock,
  quoteVariableValues: Record<QuoteVariableType, string | undefined>
): BlocknoteBlock | undefined => {
  /**
   * TS is a real pain here, and just will not accept 'hasContent' as good
   * enough for inferring correctly, so either have to cast, or use the
   * isArray check to prevent errors.
   */
  const hasContent =
    'content' in block &&
    Array.isArray(block.content) &&
    block.content.length > 0

  const hasVariable =
    'content' in block &&
    Array.isArray(block.content) &&
    block.content.find(f => f.type === 'variable')
  const hasChildren = 'children' in block && block.children.length > 0

  // Check if any children have variables
  const childrenUpdates = hasChildren
    ? block.children.map(childBlock => {
        const update = getBlockUpdate(childBlock, quoteVariableValues)
        return update ? update : childBlock
      })
    : []

  const hasChildrenWithVariables =
    hasChildren &&
    childrenUpdates.some((child, index) => child !== block.children[index])

  // Only return an update if this block or its children have variables
  if (!hasVariable && !hasChildrenWithVariables) return

  return {
    ...block,
    ...(hasContent && Array.isArray(block.content)
      ? {
          content: block.content.map(blockContent =>
            updateContent(blockContent, quoteVariableValues)
          )
        }
      : {}),
    ...(hasChildren
      ? {
          children: childrenUpdates
        }
      : {})
  } as BlocknoteBlock
}

export const updateQuoteVariableText = (
  blockNoteEditor: typeof blocknoteSchema.BlockNoteEditor,
  quoteVariableValues: Record<QuoteVariableType, string | undefined>
) => {
  /**
   * `blockNoteEditor.updateBlock` can cause a crash if called too early, and there's no reliable way of finding out
   * if Blocknote is 'ready', so we call this function until it returns.
   */
  try {
    for (const b of blockNoteEditor.document) {
      match(b)
        .with({ type: 'table' }, tableBlock => {
          const hasVariable = tableBlock.content.rows
            .flatMap(row =>
              row.cells.filter(isTableCell).flatMap(cell => cell.content)
            )
            .find(f => f.type === 'variable')
          if (!hasVariable) return

          blockNoteEditor.updateBlock(tableBlock, {
            ...tableBlock,
            content: {
              ...tableBlock.content,
              rows: tableBlock.content.rows.map(row => ({
                ...row,
                cells: row.cells.filter(isTableCell).map(cell => ({
                  ...cell,
                  content: cell.content.map((blockContent: any) =>
                    updateContent(blockContent, quoteVariableValues)
                  )
                }))
              }))
            }
          })
        })
        .otherwise(block => {
          const update = getBlockUpdate(block, quoteVariableValues)
          if (!update) return
          blockNoteEditor.updateBlock(block, update)
        })
    }
  } catch (_) {
    setTimeout(
      () => updateQuoteVariableText(blockNoteEditor, quoteVariableValues),
      5
    )
  }
}

const updateContent = (
  blockContentItem: BlocknoteInlineContent,
  quoteVariableValues: Record<QuoteVariableType, string | undefined>
): BlocknoteInlineContent => {
  if (blockContentItem.type !== 'variable') return blockContentItem

  const variableType = blockContentItem.props.variableType as QuoteVariableType

  const currentValue = blockContentItem.content
    .map((textOrLink: StyledText<StyleSchema> | Link<StyleSchema>) => {
      return match(textOrLink)
        .with({ type: 'text' }, ({ text }) => text)
        .with({ type: 'link' }, ({ content }) =>
          content.map(({ text }) => text).join('')
        )
        .exhaustive()
    })
    .join('')

  const nextValue =
    quoteVariableValues[variableType] ?? getVariableLabel(variableType)

  if (currentValue === nextValue) return blockContentItem

  return {
    ...blockContentItem,
    content: [{ type: 'text' as const, text: nextValue, styles: {} }]
  }
}
